<script setup lang="ts">
import { UiAvatar, UiIcon } from '@acme/ui'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { filterNav, navLinks } from './navFilter'
import { describeNewShortcut, handleNewShortcut } from './newShortcut'
import { useTheme } from './theme'
import type { ShellNavItem } from './types'

// Portal layout (07/10, prompt-design-ui-ux.md): dark 220px sidebar (brand, nav, footer), 48px header (context, theme,
// user menu), scrollable main on the grey page background. Below 1024px the sidebar becomes a drawer.
const props = withDefaults(defineProps<{
  brandName: string
  // Tenant logo (white label), with an optional one for the dark theme; without a logo, only the name.
  brandLogoUrl?: string | null
  brandDarkLogoUrl?: string | null
  // Square icon of the collapsed menu (and its dark theme version); without one, the initials.
  brandIconUrl?: string | null
  brandDarkIconUrl?: string | null
  context?: string
  navItems: ShellNavItem[]
  userName: string
  userDetail?: string
  userEmail?: string
  // Full-bleed page (chat): no padding or max width, and the content fills the height below the header.
  bleed?: boolean
  // The side menu can be collapsed to icons (the default). False = always open, no toggle.
  collapsible?: boolean
  // "Buscar no menu…" field above the menu (off for now in the portal).
  filterable?: boolean
}>(), { collapsible: true, filterable: false })

const emit = defineEmits<{ logout: [] }>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const theme = useTheme()

// The default sidebar is dark in both themes, so it takes the logo made for dark backgrounds. A tenant theme picks the
// menu color, and then the logo follows the light/dark mode as before.
const tenantThemed = ref(false)
let tenantThemeObserver: MutationObserver | null = null
const darkSidebar = computed(() => !tenantThemed.value || theme.isDark())
const logoSrc = computed(() => (darkSidebar.value && props.brandDarkLogoUrl) || props.brandLogoUrl || null)
const logoFailed = ref(false)
watch(logoSrc, () => (logoFailed.value = false))
const iconSrc = computed(() => (darkSidebar.value && props.brandDarkIconUrl) || props.brandIconUrl || null)
const iconFailed = ref(false)
watch(iconSrc, () => (iconFailed.value = false))

// Collapsed (icons only) is a per-person choice kept in this browser; storage can be blocked, so it is optional.
const COLLAPSED_KEY = 'aubaine.sidebar.collapsed'
function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}
const collapsedChoice = ref(readCollapsed())
const navFilter = ref('')
// While filtering, the menu shows the names (icons alone would not say what matched).
const collapsed = computed(() => props.collapsible && collapsedChoice.value && !navFilter.value.trim())
function toggleCollapsed() {
  collapsedChoice.value = !collapsedChoice.value
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsedChoice.value ? '1' : '0')
  } catch {
    // The choice just is not remembered.
  }
}

const drawerOpen = ref(false)
const menuOpen = ref(false)

// The menu button next to the logo collapses / expands the menu; in the mobile drawer it closes the drawer.
function onBrandMenuButton() {
  if (drawerOpen.value) drawerOpen.value = false
  else toggleCollapsed()
}
const menuRoot = ref<HTMLElement | null>(null)

watch(
  () => route.fullPath,
  () => {
    drawerOpen.value = false
    menuOpen.value = false
    navFilter.value = ''
    navTip.value = null
  },
)

// Collapsed menu (icons only, desktop): the name of the item under the mouse or the keyboard focus shows right next
// to it at once (the browser's title took a second and was easy to miss). Fixed, outside the sidebar, so the nav's
// scroll does not clip it.
const navTip = ref<{ label: string; top: number; left: number } | null>(null)
function showNavTip(event: Event, label: string) {
  if (!collapsed.value || !window.matchMedia('(min-width: 1024px)').matches) return
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  navTip.value = { label, top: rect.top + rect.height / 2, left: rect.right + 8 }
}
function hideNavTip() {
  navTip.value = null
}
watch(collapsed, hideNavTip)

// The item of the page: the most specific link that matches the address (15/10/2026: "Receitas e despesas"
// at erp/relatorios/receitas-e-despesas no longer lights "Overview" at erp/relatorios too).
const matches = (to: string) => route.path === to || route.path.startsWith(`${to}/`)
// An item may light up under a wider address than the page it opens (`activeUnder`: "Campanhas" opens the list of
// campaigns but stays lit on every screen of the module).
const activeTo = computed(
  () =>
    navLinks(props.navItems)
      .map((item) => ({ to: item.to, scope: item.activeUnder ?? item.to }))
      .filter((entry): entry is { to: string; scope: string } => !!entry.to && !!entry.scope && matches(entry.scope))
      .sort((a, b) => b.scope.length - a.scope.length)[0]?.to ?? null,
)
function isActive(item: ShellNavItem) {
  return !!item.to && item.to === activeTo.value
}

// ---- menu filter and groups ----
const visibleItems = computed(() => filterNav(props.navItems, navFilter.value))
const filtering = computed(() => !!navFilter.value.trim())

// Groups open and close by click; arriving at a page opens its group. While filtering (and with the
// menu collapsed to icons) every group shows its sub-items.
const openGroups = ref(new Set<string>())
const hasActiveChild = (item: ShellNavItem) => !!item.children?.some(isActive)
const isOpen = (item: ShellNavItem) => collapsed.value || filtering.value || openGroups.value.has(item.key)
function toggleGroup(item: ShellNavItem) {
  const next = new Set(openGroups.value)
  if (next.has(item.key)) next.delete(item.key)
  else next.add(item.key)
  openGroups.value = next
}
watch(
  [() => route.path, () => props.navItems],
  () => {
    const current = props.navItems.find(hasActiveChild)
    if (current && !openGroups.value.has(current.key)) openGroups.value = new Set([...openGroups.value, current.key])
  },
  { immediate: true },
)

// Enter opens the first page that matches; Escape clears the text.
function onFilterKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    const first = navLinks(visibleItems.value)[0]
    if (first?.to) {
      void router.push(first.to)
      navFilter.value = ''
      ;(event.target as HTMLInputElement).blur()
    }
  } else if (event.key === 'Escape') {
    navFilter.value = ''
  }
}

function onDocumentClick(event: MouseEvent) {
  if (menuOpen.value && menuRoot.value && !menuRoot.value.contains(event.target as Node)) {
    menuOpen.value = false
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    menuOpen.value = false
    drawerOpen.value = false
    return
  }
  // "N": the page's "Novo …" (newShortcut.ts).
  handleNewShortcut(event, document)
}
const onDescribeShortcut = (event: Event) => describeNewShortcut(event.target)

onMounted(() => {
  const root = document.documentElement
  tenantThemed.value = root.hasAttribute('data-tenant-theme')
  tenantThemeObserver = new MutationObserver(() => (tenantThemed.value = root.hasAttribute('data-tenant-theme')))
  tenantThemeObserver.observe(root, { attributes: true, attributeFilter: ['data-tenant-theme'] })
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('pointerover', onDescribeShortcut)
  document.addEventListener('focusin', onDescribeShortcut)
})
onBeforeUnmount(() => {
  tenantThemeObserver?.disconnect()
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('pointerover', onDescribeShortcut)
  document.removeEventListener('focusin', onDescribeShortcut)
})
</script>

<template>
  <div class="shell">
    <div v-if="navTip" class="shell__nav-tip" role="tooltip" :style="{ top: `${navTip.top}px`, left: `${navTip.left}px` }">{{ navTip.label }}</div>
    <div v-if="drawerOpen" class="shell__scrim" @click="drawerOpen = false"></div>

    <aside
      class="shell__sidebar"
      :class="{ 'shell__sidebar--open': drawerOpen, 'shell__sidebar--collapsed': collapsed }"
      :aria-label="t('shell.navigation')"
    >
      <div class="shell__brand">
        <div class="shell__brand-mark">
          <img v-if="logoSrc && !logoFailed" :src="logoSrc" :alt="brandName" class="shell__brand-logo" @error="logoFailed = true" />
          <span v-else class="shell__brand-name">{{ brandName }}</span>
          <template v-if="collapsed">
            <img v-if="iconSrc && !iconFailed" :src="iconSrc" :alt="brandName" class="shell__brand-mini shell__brand-icon" @error="iconFailed = true" />
            <UiAvatar v-else :name="brandName" :size="32" square class="shell__brand-mini shell__brand-avatar" />
          </template>
        </div>
        <button
          v-if="collapsible"
          type="button"
          class="shell__icon-button shell__collapse-button"
          :aria-label="drawerOpen ? t('shell.closeMenu') : collapsed ? t('shell.expandMenu') : t('shell.collapseMenu')"
          :title="drawerOpen ? t('shell.closeMenu') : collapsed ? t('shell.expandMenu') : t('shell.collapseMenu')"
          :aria-expanded="drawerOpen || !collapsed"
          @click="onBrandMenuButton"
        >
          <UiIcon name="menu" />
        </button>
        <button v-else type="button" class="shell__icon-button shell__drawer-close" :aria-label="t('shell.closeMenu')" @click="drawerOpen = false">
          <UiIcon name="menu" />
        </button>
      </div>
      <div v-if="filterable" class="shell__filter shell__filter--drawer">
        <UiIcon name="search" :size="16" class="shell__filter-icon" />
        <input
          v-model="navFilter"
          type="search"
          class="shell__filter-input"
          :placeholder="t('shell.filterMenu')"
          :aria-label="t('shell.filterMenuLabel')"
          @keydown="onFilterKeydown"
        />
      </div>
      <nav class="shell__nav" @scroll="hideNavTip">
        <template v-for="(item, index) in visibleItems" :key="item.key">
          <span v-if="item.section && item.section !== visibleItems[index - 1]?.section" class="shell__nav-section">{{ item.section }}</span>
          <template v-if="item.children?.length">
            <button
              type="button"
              class="shell__nav-item shell__nav-group"
              :class="{ 'shell__nav-group--current': hasActiveChild(item) }"
              :aria-expanded="isOpen(item)"
              :aria-controls="`shell-nav-${item.key}`"
              @click="toggleGroup(item)"
            >
              <UiIcon :name="item.icon" />
              <span class="shell__nav-label">{{ item.label }}</span>
              <UiIcon name="chevron-down" :size="13" class="shell__nav-chevron" :class="{ 'shell__nav-chevron--open': isOpen(item) }" />
            </button>
            <div v-show="isOpen(item)" :id="`shell-nav-${item.key}`" class="shell__nav-children">
              <RouterLink
                v-for="child in item.children"
                :key="child.key"
                :to="child.to ?? ''"
                class="shell__nav-item shell__nav-item--child"
                :class="{ 'shell__nav-item--active': isActive(child) }"
                :aria-current="isActive(child) ? 'page' : undefined"
                :aria-label="collapsed ? `${item.label} › ${child.label}` : undefined"
                @mouseenter="showNavTip($event, `${item.label} › ${child.label}`)"
                @mouseleave="hideNavTip"
                @focus="showNavTip($event, `${item.label} › ${child.label}`)"
                @blur="hideNavTip"
              >
                <UiIcon :name="child.icon" />
                <span class="shell__nav-label">{{ child.label }}</span>
              </RouterLink>
            </div>
          </template>
          <RouterLink
            v-else
            :to="item.to ?? ''"
            class="shell__nav-item"
            :class="{ 'shell__nav-item--active': isActive(item) }"
            :aria-current="isActive(item) ? 'page' : undefined"
            :aria-label="collapsed ? item.label : undefined"
            @mouseenter="showNavTip($event, item.label)"
            @mouseleave="hideNavTip"
            @focus="showNavTip($event, item.label)"
            @blur="hideNavTip"
          >
            <UiIcon :name="item.icon" />
            <span class="shell__nav-label">{{ item.label }}</span>
          </RouterLink>
        </template>
        <p v-if="filtering && !visibleItems.length" class="shell__nav-empty">{{ t('shell.filterMenuEmpty', { query: navFilter.trim() }) }}</p>
      </nav>
      <div v-if="$slots['sidebar-footer']" class="shell__sidebar-footer"><slot name="sidebar-footer" /></div>
    </aside>

    <div class="shell__main-column">
      <header class="shell__header">
        <div class="shell__header-start">
          <button type="button" class="shell__icon-button shell__menu-button" :aria-label="t('shell.openMenu')" @click="drawerOpen = true">
            <UiIcon name="menu" />
          </button>
          <div v-if="filterable" class="shell__filter shell__filter--header">
            <UiIcon name="search" :size="16" class="shell__filter-icon" />
            <input
              v-model="navFilter"
              type="search"
              class="shell__filter-input"
              :placeholder="t('shell.filterMenu')"
              :aria-label="t('shell.filterMenuLabel')"
              @keydown="onFilterKeydown"
            />
          </div>
          <span v-if="context" class="shell__context">{{ context }}</span>
        </div>
        <div class="shell__header-end">
          <slot name="header-actions" />
          <button
            type="button"
            class="shell__icon-button"
            :aria-label="theme.isDark() ? t('shell.switchToLight') : t('shell.switchToDark')"
            :title="theme.isDark() ? t('shell.switchToLight') : t('shell.switchToDark')"
            @click="theme.toggle()"
          >
            <UiIcon :name="theme.isDark() ? 'sun' : 'moon'" :size="18" />
          </button>
          <div class="shell__divider"></div>
          <div ref="menuRoot" class="shell__user">
            <button
              type="button"
              class="shell__user-button"
              aria-haspopup="menu"
              :aria-expanded="menuOpen"
              :aria-label="t('shell.userMenu', { name: userName })"
              @click="menuOpen = !menuOpen"
            >
              <UiAvatar :name="userName" :size="28" />
              <span class="shell__user-text">
                <span class="shell__user-name">{{ userName }}</span>
                <span v-if="userDetail" class="shell__user-detail">{{ userDetail }}</span>
              </span>
              <UiIcon name="chevron-down" :size="13" class="shell__user-chevron" :class="{ 'shell__user-chevron--open': menuOpen }" />
            </button>
            <div v-if="menuOpen" role="menu" class="shell__menu">
              <div class="shell__menu-head">
                <span class="shell__menu-name">{{ userName }}</span>
                <span v-if="userEmail" class="shell__menu-email">{{ userEmail }}</span>
                <span v-if="userDetail" class="shell__menu-detail">{{ userDetail }}</span>
              </div>
              <slot name="user-menu" />
              <button type="button" role="menuitemcheckbox" :aria-checked="theme.isDark()" class="shell__menu-item" @click="theme.toggle()">
                <UiIcon name="moon" :size="14" />
                <span class="shell__menu-grow">{{ t('shell.darkMode') }}</span>
                <span class="shell__switch" :class="{ 'shell__switch--on': theme.isDark() }" aria-hidden="true"><span></span></span>
              </button>
              <div class="shell__menu-separator"></div>
              <button type="button" role="menuitem" class="shell__menu-item shell__menu-item--danger" @click="emit('logout')">
                <UiIcon name="logout" :size="14" />
                <span>{{ t('shell.logout') }}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main class="shell__content" :class="{ 'shell__content--bleed': bleed }">
        <div v-if="bleed" class="shell__content-bleed"><slot /></div>
        <div v-else class="shell__content-inner"><slot /></div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.shell {
  display: flex;
  min-height: 100vh;
  background: var(--surface-grouped);
}
.shell__scrim {
  position: fixed;
  inset: 0;
  background: var(--scrim);
  z-index: 40;
  animation: aub-fade-in 180ms var(--ease-standard);
}
/* Sidebar: dark in both themes. The text tokens are redefined inside it, so whatever reads them (nav, footer slot)
   turns light; a tenant theme (themeCss) sets its own menu color and these same tokens. */
.shell__sidebar {
  --label: var(--nav-text-active);
  --text-primary: var(--nav-text-active);
  --secondary-label: var(--nav-text);
  --text-secondary: var(--nav-text);
  --tertiary-label: var(--nav-subtitle);
  --separator: var(--nav-divider);
  --accent: var(--nav-text-active);
  --on-accent: #0a0a0a;
  --tint-accent-bg: var(--nav-active-bg);
  --tint-accent-strong-bg: var(--nav-active-bg);
  width: var(--sidebar-width);
  min-width: var(--sidebar-width);
  background: var(--nav-bg);
  color: var(--text-primary);
  border-right: 1px solid var(--separator);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 50;
}
.shell__brand {
  min-height: 72px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 20px 8px 20px 20px;
  border-bottom: 1px solid var(--separator);
}
.shell__brand-mark {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}
.shell__brand-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  object-fit: contain;
}
.shell__brand-logo {
  max-width: 100%;
  max-height: 32px;
  object-fit: contain;
}
.shell__brand-name {
  font-size: var(--type-body-size);
  font-weight: var(--weight-semibold);
  letter-spacing: 0.01em;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shell__nav {
  flex: 1;
  overflow-y: auto;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.shell__nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  padding: 0 12px;
  border-radius: var(--radius-control);
  color: var(--text-secondary);
  font-size: var(--type-nav-size);
  font-weight: var(--weight-medium);
  text-decoration: none;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);
}
.shell__nav-item :deep(svg) {
  width: 15px;
  height: 15px;
  stroke-width: 1.8;
  flex: none;
}
.shell__nav-section {
  margin: 14px 12px 4px;
  font-size: var(--type-caption-2-size);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--tertiary-label);
  font-weight: var(--weight-semibold);
}
.shell__nav-item:hover {
  background: color-mix(in srgb, currentColor 8%, transparent);
  color: var(--text-primary);
  text-decoration: none;
}
.shell__nav-item--active,
.shell__nav-item--active:hover {
  background: var(--tint-accent-bg);
  color: var(--accent);
}
.shell__nav-group {
  width: 100%;
  border: none;
  background: transparent;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}
.shell__nav-group .shell__nav-label {
  flex: 1;
}
.shell__nav-group--current {
  color: var(--text-primary);
}
.shell__nav-item :deep(svg.shell__nav-chevron) {
  width: 13px;
  height: 13px;
  transition: transform var(--duration-fast) var(--ease-standard);
}
.shell__nav-chevron--open {
  transform: rotate(180deg);
}
.shell__nav-children {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 19px;
  padding-left: 6px;
  border-left: 1px solid var(--separator);
}
.shell__nav-item--child {
  min-height: 36px;
}
.shell__nav-empty {
  margin: 8px 12px;
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
/* Menu filter: in the header next to the menu button; inside the drawer on small screens. */
.shell__filter {
  position: relative;
  display: flex;
  align-items: center;
}
.shell__filter-icon {
  position: absolute;
  left: 12px;
  color: var(--secondary-label);
  pointer-events: none;
}
.shell__filter-input {
  width: 260px;
  height: var(--control-height-sm);
  padding: 0 12px 0 34px;
  border: 1px solid var(--separator);
  border-radius: var(--radius-control);
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: var(--type-body-size);
}
.shell__filter-input::placeholder {
  color: var(--secondary-label);
}
.shell__filter-input:focus {
  outline: none;
  border-color: var(--accent);
}
.shell__filter--drawer {
  display: none;
  padding: 12px 12px 0;
}
.shell__filter--drawer .shell__filter-icon {
  left: 24px;
}
.shell__filter--drawer .shell__filter-input {
  width: 100%;
}
.shell__sidebar-footer {
  padding: 16px 20px;
  border-top: 1px solid var(--separator);
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: var(--type-footnote-size);
}
.shell__main-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.shell__header {
  height: var(--header-height);
  min-height: var(--header-height);
  background: var(--surface-card);
  border-bottom: 1px solid var(--separator-soft);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 30;
}
.shell__header-start,
.shell__header-end {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.shell__header-end {
  margin-left: auto;
}
.shell__context {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shell__icon-button {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  color: var(--tertiary-label);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);
}
.shell__icon-button:hover {
  background: color-mix(in srgb, currentColor 10%, transparent);
  color: var(--text-primary);
}
.shell__sidebar .shell__icon-button {
  color: var(--text-secondary);
}
.shell__menu-button {
  display: none;
}
.shell__divider {
  width: 1px;
  height: 20px;
  background: var(--separator-soft);
}
.shell__user {
  position: relative;
}
.shell__user-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 6px 2px 2px;
  min-height: 36px;
  border: none;
  background: transparent;
  border-radius: var(--radius-control);
  color: var(--text-primary);
  cursor: pointer;
  text-align: left;
}
.shell__user-button:hover {
  background: var(--surface-hover);
}
.shell__user-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.shell__user-name {
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
}
.shell__user-detail {
  font-size: var(--type-caption-1-size);
  color: var(--tertiary-label);
}
.shell__user-chevron {
  color: var(--tertiary-label);
  transition: transform var(--duration-fast) var(--ease-standard);
}
.shell__user-chevron--open {
  transform: rotate(180deg);
}
.shell__menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 60;
  width: 260px;
  background: var(--surface-card);
  border: var(--hairline) solid var(--separator);
  border-radius: var(--radius-popover);
  box-shadow: var(--shadow-md);
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  animation: aub-fade-in 120ms var(--ease-standard);
}
.shell__menu-head {
  padding: 8px 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  border-bottom: 1px solid var(--separator-soft);
  margin-bottom: 4px;
}
.shell__menu-name {
  font-size: var(--type-body-size);
  font-weight: var(--weight-semibold);
}
.shell__menu-email {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shell__menu-detail {
  font-size: var(--type-caption-1-size);
  color: var(--tertiary-label);
}
.shell__menu-item,
.shell__menu :deep(.shell-menu-item) {
  min-height: 34px;
  margin: 0 4px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  text-align: left;
  color: var(--text-label);
  font-size: var(--type-body-size);
  font-weight: var(--weight-regular);
  cursor: pointer;
  text-decoration: none;
}
.shell__menu-item :deep(svg),
.shell__menu :deep(.shell-menu-item svg) {
  width: 14px;
  height: 14px;
  color: var(--tertiary-label);
}
.shell__menu-item:hover,
.shell__menu :deep(.shell-menu-item:hover) {
  background: var(--surface-hover);
  text-decoration: none;
}
.shell__menu-item--danger,
.shell__menu-item--danger :deep(svg) {
  color: var(--danger);
}
.shell__menu-grow {
  flex: 1;
}
.shell__menu-separator {
  height: 1px;
  background: var(--separator-soft);
  margin: 4px 0;
}
.shell__switch {
  width: 28px;
  height: 16px;
  border-radius: var(--radius-full);
  background: var(--system-gray-3);
  position: relative;
  transition: background var(--duration-fast) var(--ease-standard);
}
.shell__switch span {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ffffff;
  transition: left var(--duration-fast) var(--ease-standard);
}
.shell__switch--on {
  background: var(--accent);
}
.shell__switch--on span {
  left: 14px;
  background: var(--on-accent);
}
.shell__content {
  flex: 1;
  padding: 24px;
}
.shell__content--bleed {
  flex: 0 0 auto;
  padding: 0;
  display: flex;
  min-height: 0;
  overflow: hidden;
  height: calc(100vh - var(--header-height));
  height: calc(100dvh - var(--header-height));
}
.shell__content-bleed {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
}
/* No global max width: the content takes the whole width (forms limit themselves). */
.shell__content-inner {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.shell__brand .shell__brand-mini {
  display: none;
}
.shell__collapse-button {
  display: inline-flex;
  flex: none;
}
.shell__drawer-close {
  display: none;
}
/* The name of an item of the collapsed menu, next to it (showNavTip). */
.shell__nav-tip {
  position: fixed;
  z-index: 70;
  transform: translateY(-50%);
  padding: 5px 9px;
  border-radius: var(--radius-sm, 6px);
  background: var(--text-primary);
  color: var(--surface);
  font-size: var(--type-footnote-size);
  font-weight: var(--weight-semibold);
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.18);
}
/* Collapsed: icons only (their names show beside them on hover/focus). The mobile drawer always stays complete. */
@media (min-width: 1024px) {
  .shell__sidebar {
    transition:
      width var(--duration-base) var(--ease-standard),
      min-width var(--duration-base) var(--ease-standard);
  }
  /* Room for the icon and the menu button side by side. */
  .shell__sidebar--collapsed {
    width: 92px;
    min-width: 92px;
  }
  .shell__sidebar--collapsed .shell__brand {
    padding: 20px 6px 20px 12px;
  }
  .shell__sidebar--collapsed .shell__brand-logo,
  .shell__sidebar--collapsed .shell__brand-name {
    display: none;
  }
  .shell__sidebar--collapsed .shell__brand .shell__brand-mini {
    display: inline-flex;
  }
  .shell__sidebar--collapsed .shell__nav-item {
    justify-content: center;
    padding: 0;
  }
  .shell__sidebar--collapsed .shell__nav-label,
  .shell__sidebar--collapsed .shell__nav-section,
  .shell__sidebar--collapsed .shell__nav-group,
  .shell__sidebar--collapsed .shell__sidebar-footer {
    display: none;
  }
  /* Collapsed, the sub-items show as plain icons. */
  .shell__sidebar--collapsed .shell__nav-children {
    margin-left: 0;
    padding-left: 0;
    border-left: none;
  }
}
@media (max-width: 1023px) {
  .shell__drawer-close {
    display: inline-flex;
  }
  .shell__sidebar {
    position: fixed;
    left: 0;
    top: 0;
    transform: translateX(-100%);
    transition: transform 300ms ease-in-out;
  }
  .shell__sidebar--open {
    transform: none;
  }
  .shell__nav-item {
    min-height: 44px;
  }
  .shell__header {
    padding: 0 12px;
  }
  .shell__menu-button {
    display: inline-flex;
    width: 44px;
    height: 44px;
    color: var(--text-label);
  }
  .shell__menu-button :deep(svg) {
    width: 22px;
    height: 22px;
  }
  .shell__filter--header {
    display: none;
  }
  .shell__filter--drawer {
    display: flex;
  }
  .shell__content {
    padding: 16px;
  }
  .shell__content--bleed {
    padding: 0;
  }
}
@media (max-width: 719px) {
  .shell__user-text,
  .shell__user-chevron {
    display: none;
  }
}
</style>
